import { WDD } from '../core/wdd.js';

export function prices() {
    const page = WDD.el.querySelector('.bp-main');
    const panels = [...WDD.el.querySelectorAll('[data-summary-model]')];

    if (!page || !panels.length) return;

    const catalog = new Map();
    const discounts = new Map();

    const getCodes = element => (element?.dataset.productCodes || '').trim().toUpperCase().split(/\s+/).filter(Boolean);
    const activeChoice = (panel, selector) => panel.querySelector(`${selector}.bp-button--active`);

    // Product codes allowed by the selected storage (models without storage choice allow everything)
    function storageCodes(panel) {
        const storage = activeChoice(panel, '[data-storage-choice]');
        return storage ? new Set(getCodes(storage)) : null;
    }

    function resolveProduct(panel) {
        const allowed = storageCodes(panel);
        const codes = getCodes(activeChoice(panel, '[data-color-choice]')).filter(code => !allowed || allowed.has(code));
        const matches = codes.map(code => catalog.get(code)).filter(Boolean);

        if (matches.length !== 1) {
            throw new Error(`Expected one catalog item for ${codes.join(', ') || 'the selected variant'}, found ${matches.length}.`);
        }

        return matches[0];
    }

    function updateAvailability(panel) {
        const inCatalog = code => !catalog.size || catalog.has(code);
        const allowed = storageCodes(panel);
        const colorCodes = new Set(getCodes(activeChoice(panel, '[data-color-choice]')));

        panel.querySelectorAll('[data-color-choice]').forEach(choice => {
            choice.disabled = !getCodes(choice).some(code => (!allowed || allowed.has(code)) && inCatalog(code));
        });

        panel.querySelectorAll('[data-storage-choice]').forEach(choice => {
            choice.disabled = !getCodes(choice).some(code => colorCodes.has(code) && inCatalog(code));
        });
    }

    // Final price comes from the database. Crossed-out price prefers comparePrice (regular sale),
    // otherwise the voucher discount from HTML is used, but only if the database offers a voucher code.
    function updatePrice(panel, item) {
        const button = panel.querySelector('.jsBuy');
        const price = panel.querySelector('[data-current-price]');
        const original = panel.querySelector('[data-original-price]');
        const label = panel.querySelector('[data-discount-label]');
        const discount = item.voucher ? discounts.get(item.uid) : undefined;
        const originalText = item.comparePrice || (discount ? WDD.utils.formatPrice(item.price + discount) : '');

        if (button.dataset.uid !== item.uid) {
            button.classList.remove('bp-button--inBasket');
            button.setAttribute('aria-label', button.dataset.text);
        }

        price.textContent = WDD.utils.formatPrice(item.price);
        original.hidden = !originalText;
        original.textContent = originalText;
        label.hidden = !discount;
        label.textContent = discount ? `${label.dataset.text} ${WDD.utils.formatPrice(discount)}` : '';

        if (item.voucher) button.dataset.voucher = item.voucher;
        else delete button.dataset.voucher;

        button.dataset.oid = String(item.oid);
        button.dataset.uid = item.uid;
        button.disabled = button.classList.contains('bp-button--adding') || button.classList.contains('bp-button--inBasket');
    }

    function refreshProduct(panel) {
        updateAvailability(panel);
        if (!catalog.size) return;

        try {
            updatePrice(panel, resolveProduct(panel));
        } catch (error) {
            console.error(`Unable to resolve the selected product for ${panel.dataset.summaryModel}:`, error);
            panel.querySelector('.jsBuy').disabled = true;
        }
    }

    function loadDiscounts() {
        page.querySelectorAll('[data-product-code][data-discount]').forEach(element => {
            const code = element.dataset.productCode.toUpperCase();
            const value = Number(element.dataset.discount);
            if (!Number.isFinite(value) || value <= 0) throw new Error(`Invalid discount value for product ${code}.`);
            discounts.set(code, value);
        });
    }

    async function loadCatalog() {
        const payload = await WDD.utils.loadCommodityPack(page.dataset.commodityPack);
        if (!Array.isArray(payload?.items)) throw new Error('The product catalog does not contain an items array.');

        payload.items.forEach(item => {
            const uid = typeof item.uid === 'string' ? item.uid.toUpperCase() : '';
            const oid = item.oid;
            const priceTag = item.priceInfoV3?.mainPriceTag;
            const price = Number(priceTag?.primaryPriceNoCurrency);

            if (!uid || !Number.isInteger(oid) || oid <= 0 || !Number.isFinite(price) || price <= 0) {
                throw new Error(`The product catalog contains an invalid item: ${item.name || 'unknown product'}.`);
            }
            if (catalog.has(uid)) throw new Error(`Duplicate product code in catalog: ${uid}.`);

            catalog.set(uid, {
                uid,
                oid,
                price,
                comparePrice: priceTag.comparePrice || null,
                voucher: priceTag.codeToBuyWith || null
            });
        });
    }

    function showFatalError(error) {
        catalog.clear();
        console.error('Unable to load prices of the OPPO Find X10 page:', error);
        panels.forEach(panel => {
            panel.querySelector('.jsBuy').disabled = true;
        });
    }

    WDD.el.addEventListener('bp:variantchange', event => refreshProduct(event.target));
    panels.forEach(updateAvailability);

    loadCatalog()
        .then(() => {
            loadDiscounts();
            panels.forEach(refreshProduct);
        })
        .catch(showFatalError);
}
